import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { NutritionalValues, ProductNutritionalInfo } from '../types';

interface NutritionalDataTabProps {
  nutritionalInfo: ProductNutritionalInfo;
  onChange: (nutritionalInfo: ProductNutritionalInfo) => void;
  disabled?: boolean;
}

type NutrientColumn = 'per100g' | 'perServing';

const NutritionalDataTab: FC<NutritionalDataTabProps> = ({ nutritionalInfo, onChange, disabled }) => {
  const { t } = useTranslation('productManagement');

  const rows: { key: keyof NutritionalValues; label: string; unit: string }[] = [
    { key: 'energyKj', label: t('energyKj', 'Energy (kJ)'), unit: 'kJ' },
    { key: 'energyKcal', label: t('energyKcal', 'Energy (kcal)'), unit: 'kcal' },
    { key: 'fat', label: t('fat', 'Fat'), unit: 'g' },
    { key: 'saturates', label: t('saturates', 'of which saturates'), unit: 'g' },
    { key: 'carbohydrate', label: t('carbohydrate', 'Carbohydrate'), unit: 'g' },
    { key: 'sugars', label: t('sugars', 'of which sugars'), unit: 'g' },
    { key: 'fibre', label: t('fibre', 'Fibre'), unit: 'g' },
    { key: 'protein', label: t('protein', 'Protein'), unit: 'g' },
    { key: 'salt', label: t('salt', 'Salt'), unit: 'g' },
  ];

  const handleValueChange = (column: NutrientColumn, key: keyof NutritionalValues, value: string) => {
    onChange({
      ...nutritionalInfo,
      [column]: {
        ...nutritionalInfo[column],
        [key]: value === '' ? undefined : Number(value),
      },
    });
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="textLG"
        sx={{ mb: 4, fontWeight: 600 }}>
        {t('nutritionalInfo', 'Nutritional Info')}
      </Typography>
      <Grid container
        spacing={4}
        sx={{ mb: 4 }}>
        <Grid item
          xs={12}
          md={4}>
          <TextField
            label={t('servingSize', 'Serving Size')}
            value={nutritionalInfo.servingSize ?? ''}
            onChange={(e) => onChange({ ...nutritionalInfo, servingSize: e.target.value })}
            disabled={disabled}
            placeholder={t('servingSizePlaceholder', 'e.g. 100g')}
            fullWidth
          />
        </Grid>
      </Grid>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('nutrient', 'Nutrient')}</TableCell>
              <TableCell>{t('per100g', 'Per 100g')}</TableCell>
              <TableCell>{t('perServing', 'Per Serving')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{row.label}</TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={nutritionalInfo.per100g[row.key] ?? ''}
                    onChange={(e) => handleValueChange('per100g', row.key, e.target.value)}
                    disabled={disabled}
                    size="small"
                    InputProps={{ endAdornment: <InputAdornment position="end">{row.unit}</InputAdornment> }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={nutritionalInfo.perServing[row.key] ?? ''}
                    onChange={(e) => handleValueChange('perServing', row.key, e.target.value)}
                    disabled={disabled}
                    size="small"
                    InputProps={{ endAdornment: <InputAdornment position="end">{row.unit}</InputAdornment> }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NutritionalDataTab;
